import React, { useState } from 'react';
import { Download, Copy, Eye, Palette } from 'lucide-react';

export const BrandElements: React.FC = () => {
  const [selectedElement, setSelectedElement] = useState<string>('logos');

  const brandElements = {
    logos: {
      title: 'Logos & Wordmarks',
      description: 'Primary brand marks and variations',
      elements: [
        {
          name: 'Primary Logo',
          description: 'Full logo with wordmark - use for headers and primary branding',
          preview: (
            <div className="bg-[var(--bg-raised)] p-8 border-2 border-[var(--rule)]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-consulting-slate-blue-600 to-sage-600 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">P</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-charcoal-900">Peak Priorities</h3>
                  <p className="text-sm text-charcoal-600">Decision Intelligence</p>
                </div>
              </div>
            </div>
          )
        },
        {
          name: 'Icon Only',
          description: 'Standalone icon for favicons and compact spaces',
          preview: (
            <div className="bg-[var(--bg-raised)] p-8 border-2 border-[var(--rule)]">
              <div className="w-16 h-16 bg-gradient-to-br from-consulting-slate-blue-600 to-sage-600 flex items-center justify-center mx-auto">
                <span className="text-white font-bold text-2xl">P</span>
              </div>
            </div>
          )
        },
        {
          name: 'Wordmark Only',
          description: 'Text-only version for horizontal layouts',
          preview: (
            <div className="bg-[var(--bg-raised)] p-8 border-2 border-[var(--rule)]">
              <h2 className="text-3xl font-bold text-charcoal-900">Peak Priorities</h2>
            </div>
          )
        }
      ]
    },
    patterns: {
      title: 'Patterns & Textures',
      description: 'Background patterns and visual textures',
      elements: [
        {
          name: 'Mountain Pattern',
          description: 'Subtle mountain silhouette pattern for backgrounds',
          preview: (
            <div className="bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-[var(--rule)] relative overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <path d="M0,80 L20,60 L40,70 L60,40 L80,50 L100,30 L100,100 L0,100 Z" fill="currentColor" className="text-consulting-slate-blue-600"/>
                </svg>
              </div>
              <div className="relative z-10">
                <h3 className="text-lg font-semibold text-charcoal-900">Mountain Pattern</h3>
                <p className="text-sm text-charcoal-600">Use at 10% opacity for subtle backgrounds</p>
              </div>
            </div>
          )
        },
        {
          name: 'Grid Pattern',
          description: 'Geometric grid for structured layouts',
          preview: (
            <div className="bg-[var(--bg-raised)] p-8 border-2 border-[var(--rule)] relative">
              <div className="absolute inset-0 opacity-20">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                      <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-consulting-slate-blue-600"/>
                    </pattern>
                  </defs>
                  <rect width="100" height="100" fill="url(#grid)" />
                </svg>
              </div>
              <div className="relative z-10">
                <h3 className="text-lg font-semibold text-charcoal-900">Grid Pattern</h3>
                <p className="text-sm text-charcoal-600">Use at 20% opacity for structure</p>
              </div>
            </div>
          )
        }
      ]
    },
    graphics: {
      title: 'Illustrations & Graphics',
      description: 'Custom illustrations and graphic elements',
      elements: [
        {
          name: 'Architecture Diagram',
          description: 'M4-M0 layer visualization',
          preview: (
            <div className="bg-[var(--bg-raised)] p-8 border-2 border-[var(--rule)]">
              <div className="space-y-2">
                {['Engines', 'M3: Metadata', 'M2: Models', 'M1: Templates', 'M0: Runtime'].map((layer, i) => (
                  <div key={layer} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold ${
                      i === 0 ? 'bg-[var(--slate)]' : 
                      i === 1 ? 'bg-[var(--slate)]' : 
                      i === 2 ? 'bg-consulting-slate-blue-600' : 
                      i === 3 ? 'bg-sage-600' : 'bg-[var(--mustard)]'
                    }`}>
                      {i + 1}
                    </div>
                    <span className="text-sm font-medium text-charcoal-900">{layer}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        },
        {
          name: 'Meta-Stack Visualization',
          description: 'Four-dimensional framework representation',
          preview: (
            <div className="bg-[var(--bg-raised)] p-8 border-2 border-[var(--rule)]">
              <div className="grid grid-cols-2 gap-3">
                {['Ontology', 'Modality', 'Dynamics', 'Evaluation'].map((dimension, i) => (
                  <div key={dimension} className={`p-3  text-center ${
                    i === 0 ? 'bg-[var(--mustard-dim)] border-2 border-[var(--mustard)]' :
                    i === 1 ? 'bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200' :
                    i === 2 ? 'bg-sage-50 border-2 border-sage-200' : 'bg-[var(--mustard-dim)] border-2 border-[var(--mustard)]'
                  }`}>
                    <h4 className="font-semibold text-sm text-charcoal-900">{dimension}</h4>
                  </div>
                ))}
              </div>
            </div>
          )
        }
      ]
    }
  };

  const currentCategory = brandElements[selectedElement as keyof typeof brandElements];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Palette className="h-10 w-10 text-[var(--mustard)]" />
          <h1 className="text-4xl font-bold text-charcoal-900">Brand Elements</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          Visual identity elements that reinforce the Peak Priorities brand across all touchpoints.
        </p>
      </div>

      {/* Category Navigation */}
      <div className="flex flex-wrap justify-center gap-2">
        {Object.entries(brandElements).map(([key, category]) => (
          <button
            key={key}
            onClick={() => setSelectedElement(key)}
            className={`px-6 py-3  font-semibold transition-colors ${
              selectedElement === key
                ? 'bg-[var(--mustard)] text-white'
                : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }`}
          >
            {category.title}
          </button>
        ))}
      </div>

      {/* Category Content */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-2">{currentCategory.title}</h2>
          <p className="text-charcoal-700">{currentCategory.description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {currentCategory.elements.map((element, index) => (
            <div key={index} className="space-y-4">
              <div>
                <h3 className="font-bold text-charcoal-900 mb-2">{element.name}</h3>
                <p className="text-sm text-charcoal-600 mb-4">{element.description}</p>
              </div>
              
              {element.preview}
              
              <div className="flex gap-2">
                <button className="flex items-center gap-2 px-3 py-2 bg-consulting-slate-blue-600 text-white text-sm hover:bg-consulting-slate-blue-700 transition-colors">
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-[var(--hover-tint)] text-charcoal-700 text-sm hover:bg-[var(--hover-tint)] transition-colors">
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-[var(--hover-tint)] text-charcoal-700 text-sm hover:bg-[var(--hover-tint)] transition-colors">
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Brand Guidelines</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-charcoal-900 mb-3">Logo Usage</h3>
            <ul className="space-y-2 text-sm text-charcoal-700">
              <li>• Maintain minimum clear space around logos</li>
              <li>• Use primary logo for headers and main branding</li>
              <li>• Icon-only version for favicons and compact spaces</li>
              <li>• Never distort or modify logo proportions</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-bold text-charcoal-900 mb-3">Pattern Usage</h3>
            <ul className="space-y-2 text-sm text-charcoal-700">
              <li>• Use patterns at low opacity (10-20%)</li>
              <li>• Mountain pattern for nature/outdoor contexts</li>
              <li>• Grid pattern for structured/technical content</li>
              <li>• Ensure patterns don't interfere with readability</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
